import { inject, Injectable } from '@angular/core'

import { LoggerFactory } from 'common/log'

const require = (window as any).nodeRequire

/**
 * Opaque token for a live Premiere object held by the plugin. Returned by the
 * `api.*` bridge (the object itself cannot cross the WebSocket); pass it back
 * to operate on that object.
 */
export interface UxpHandle {
  __ref: string
  __type?: string
  __data?: Record<string, unknown>
}

function isHandle (value: unknown): value is UxpHandle {
  return typeof value === 'object' && value !== null && '__ref' in value
}

/**
 * A creator call for {@link UxpBridgeService.rpcExecuteActions}. The plugin
 * runs `target[method](...args)` inside the transaction lock, decoding any
 * handle refs in `args` back into live objects.
 */
export interface ActionSpec {
  /** Handle owning the creator. Omit to target the project. */
  target?: UxpHandle
  method: string
  args?: unknown[]
}

export interface CallOpts {
  ref?: UxpHandle
  /** Wrap in Premiere's executeAsModal. */
  modal?: boolean
  modalText?: string
  /** Wrap in project.lockedAccess (needs {@link lockProject}). */
  locked?: boolean
  lockProject?: UxpHandle
  timeoutMs?: number
}

/**
 * Error raised when a bridge RPC fails. Attaches the RPC method and Premiere
 * API path so logs point at the failing call rather than just a bare native
 * string like "Illegal Parameter type".
 */
export class BridgeError extends Error {
  constructor (
    public readonly rpcMethod: string,
    public readonly apiPath: string | undefined,
    public readonly cause: unknown,
    /** True when the transport/plugin was unavailable (vs. an API error). */
    public readonly transport = false,
  ) {
    const detail = cause instanceof Error ? cause.message : String(cause)
    const where = apiPath ? `${rpcMethod} ${apiPath}` : rpcMethod
    super(`UXP bridge ${where}: ${detail}`)
    this.name = 'BridgeError'
  }

  get userMessage (): string {
    const inner = this.cause
    const message = inner instanceof Error ? inner.message : String(inner)
    return message.replace(/^(Error:\s*)+/i, '').trim()
  }
}

/** Every RPC payload is an object; some carry a `path` we surface in errors. */
type RpcPayload = { path?: string } & Record<string, unknown>

/**
 * Renderer-side transport to the headless Premiere UXP plugin. The panel runs
 * in a different process from Premiere, so every call is routed:
 *
 *   panel renderer  --IPC-->  main process  --WS-->  UXP plugin  -->  Premiere
 *
 * Speaks the plugin's generic `api.*` reflection protocol and wraps returned
 * handles in {@link Ref} for fluent chaining.
 */
@Injectable({ providedIn: 'root' })
export class UxpBridgeService {
  private readonly logger = inject(LoggerFactory).create('UxpBridge')
  private readonly ipc = this.getIpc()

  private getIpc (): any {
    try {
      return require('electron').ipcRenderer
    } catch {
      return null
    }
  }

  /** True when running inside the ELEMENTS client (i.e. an IPC channel exists). */
  get hasTransport (): boolean {
    return Boolean(this.ipc)
  }

  async isAvailable (): Promise<boolean> {
    if (!this.ipc) {
      return false
    }
    try {
      return await this.ipc.invoke('panel-bridge:available')
    } catch (err) {
      this.logger.warn('availability check failed', err)
      return false
    }
  }

  /** @returns unsubscribe function */
  onAvailabilityChange (handler: (available: boolean) => void): () => void {
    if (!this.ipc) {
      return () => undefined
    }
    const listener = (_event: unknown, data: { available: boolean }) => handler(data.available)
    this.ipc.on('panel-bridge:availability', listener)
    return () => this.ipc.removeListener('panel-bridge:availability', listener)
  }

  /** @returns unsubscribe function */
  onPluginEvent (handler: (event: string, payload: unknown) => void): () => void {
    if (!this.ipc) {
      return () => undefined
    }
    const listener = (_e: unknown, data: { event: string, payload: unknown }) => handler(data.event, data.payload)
    this.ipc.on('panel-bridge:plugin-event', listener)
    return () => this.ipc.removeListener('panel-bridge:plugin-event', listener)
  }

  private async rpc<R = unknown> (method: string, payload: RpcPayload, timeoutMs?: number): Promise<R> {
    if (!this.ipc) {
      throw new BridgeError(
        method,
        payload.path,
        'transport unavailable (not running in the ELEMENTS client)',
        true,
      )
    }
    try {
      return await this.ipc.invoke('panel-bridge:call', { method, payload, timeoutMs })
    } catch (error) {
      const message = error instanceof Error ? error.message : ''
      const isTransportError
        = message.includes('plugin disconnected')
        || message.includes('not connected')
        || message.includes('Error invoking remote method')
      const bridgeError = new BridgeError(method, payload.path, error, isTransportError)
      // Log here so failures are recorded even when a caller swallows them
      // with `.catch(() => fallback)`.
      this.logger.warn(bridgeError.message)
      throw bridgeError
    }
  }

  /** Read a property; the result is a primitive or a handle token. */
  get<R = unknown> (path: string, ref?: UxpHandle): Promise<R> {
    return this.rpc<R>('api.get', { path, ref })
  }

  set (path: string, value: unknown, ref?: UxpHandle): Promise<void> {
    return this.rpc('api.set', { path, value, ref })
  }

  call<R = unknown> (
    path: string,
    args: unknown[] = [],
    opts: CallOpts = {},
  ): Promise<R> {
    return this.rpc<R>('api.call', {
      path,
      args,
      ref: opts.ref,
      modal: opts.modal,
      modalText: opts.modalText,
      locked: opts.locked,
      lockProject: opts.lockProject,
    }, opts.timeoutMs)
  }

  async construct (path: string, args: unknown[] = []): Promise<UxpHandle> {
    const result = await this.rpc('api.new', { path, args })
    if (!isHandle(result)) {
      throw new Error(`api.new("${path}") did not return a handle`)
    }
    return result
  }

  release (ref: UxpHandle): Promise<void> {
    return this.rpc('api.release', { ref }).then(() => undefined)
  }

  /** Errors are swallowed: best-effort, and a disconnect clears the registry anyway. */
  async releaseMany (refs: UxpHandle[]): Promise<void> {
    if (!refs.length) {
      return
    }
    try {
      await this.rpc('api.releaseMany', { refs })
    } catch (error) {
      this.logger.debug('releaseMany failed (ignored)', error)
    }
  }

  /**
   * Build and commit action specs in one undo transaction. The plugin builds
   * every action and runs executeTransaction inside a single lockedAccess
   * scope, since state-reading creators require the lock and it cannot span
   * async RPC round-trips (so we cannot pre-build actions here).
   */
  rpcExecuteActions (project: UxpHandle, actions: ActionSpec[], undoString?: string): Promise<{ ok: boolean, committed: number }> {
    return this.rpc('api.executeActions', { project, actions, undoString })
  }

  private readonly scopes: RefScope[] = []

  /** Record a freshly-created Ref in the current scope, if any. */
  track (ref: Ref): Ref {
    this.scopes[this.scopes.length - 1]?.add(ref)
    return ref
  }

  /**
   * Run `fn` in a handle scope: every Ref created during it is released in one
   * batch when `fn` settles. Pass Refs that must outlive the scope (e.g. a
   * returned result) to `scope.keep()`; scopes nest, so a kept Ref is promoted
   * to the enclosing scope rather than leaking.
   */
  async withScope<T> (fn: (scope: RefScope) => Promise<T>): Promise<T> {
    const scope = new RefScope()
    this.scopes.push(scope)
    try {
      return await fn(scope)
    } finally {
      this.scopes.pop()
      const parent = this.scopes[this.scopes.length - 1]
      await scope.dispose(this, parent)
    }
  }

  ref (handle: UxpHandle): Ref {
    return this.track(new Ref(this, handle))
  }

  /** Resolve a root path (e.g. "premierepro.Project") to a Ref. */
  async root (path: string): Promise<Ref> {
    const value = await this.get(path)
    if (!isHandle(value)) {
      throw new Error(`"${path}" is not an object handle`)
    }
    return this.track(new Ref(this, value))
  }

  /** Resolve a `premierepro.*` root object (Project, Markers, TickTime, ...). */
  ppro (name: string): Promise<Ref> {
    return this.root(`premierepro.${name}`)
  }

  static asHandle (value: unknown): UxpHandle | null {
    return isHandle(value) ? value : null
  }

  static isHandle = isHandle
}

/**
 * Fluent wrapper over a single remote handle. Methods resolve to primitives or
 * further Refs, keeping interface code close to synchronous ExtendScript.
 */
export class Ref {
  constructor (
    private readonly bridge: UxpBridgeService,
    public readonly handle: UxpHandle,
  ) {}

  get type (): string | undefined {
    return this.handle.__type
  }

  get<R = unknown> (prop: string): Promise<R> {
    return this.bridge.get<R>(prop, this.handle)
  }

  /** Object-valued property as a Ref, or null. */
  async getRef (prop: string): Promise<Ref | null> {
    const value = await this.bridge.get(prop, this.handle)
    return isHandle(value) ? this.bridge.ref(value) : null
  }

  set (prop: string, value: unknown): Promise<void> {
    return this.bridge.set(prop, value, this.handle)
  }

  call<R = unknown> (method: string, args: unknown[] = [], opts: Omit<CallOpts, 'ref'> = {}): Promise<R> {
    return this.bridge.call<R>(method, args, { ...opts, ref: this.handle })
  }

  /** Method result as a Ref, or null. */
  async callRef (method: string, args: unknown[] = [], opts: Omit<CallOpts, 'ref'> = {}): Promise<Ref | null> {
    const value = await this.bridge.call(method, args, { ...opts, ref: this.handle })
    return isHandle(value) ? this.bridge.ref(value) : null
  }

  /** Method result as Refs; non-handle entries dropped. */
  async callRefList (method: string, args: unknown[] = [], opts: Omit<CallOpts, 'ref'> = {}): Promise<Ref[]> {
    const value = await this.bridge.call(method, args, { ...opts, ref: this.handle })
    if (!Array.isArray(value)) {
      return []
    }
    return value.filter(isHandle).map(h => this.bridge.ref(h))
  }

  release (): Promise<void> {
    return this.bridge.release(this.handle)
  }
}

/**
 * Tracks Refs created within a {@link UxpBridgeService.withScope} block so
 * their handles are released in one batch when it ends. One release per
 * creation matches the plugin's refcount, so a shared object survives until
 * every Ref to it is released. {@link keep} excludes a Ref and promotes it to
 * the parent scope so a returned result isn't freed early.
 */
export class RefScope {
  private readonly tracked: Ref[] = []
  private readonly kept = new Set<Ref>()

  add (ref: Ref): void {
    this.tracked.push(ref)
  }

  /** Retain a Ref so it survives the scope. */
  keep<T extends Ref | null | undefined> (ref: T): T {
    if (ref) {
      this.kept.add(ref)
    }
    return ref
  }

  keepAll<T extends Ref> (refs: T[]): T[] {
    for (const ref of refs) {
      this.kept.add(ref)
    }
    return refs
  }

  async dispose (bridge: UxpBridgeService, parent?: RefScope): Promise<void> {
    const toRelease: UxpHandle[] = []
    for (const ref of this.tracked) {
      if (this.kept.has(ref)) {
        parent?.add(ref)
      } else {
        toRelease.push(ref.handle)
      }
    }
    await bridge.releaseMany(toRelease)
  }
}
